import { FunctionComponent, useCallback, useState } from "react";

import { useTranslation } from "react-i18next";

import { Autocomplete, TextField } from "@mui/material";

import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Modal } from "@/components/Modals/Modal";

import { useSearch } from "@/utils";

import { useGetCsvEnrichmentsQuery } from "@/pages/Private/redux/csvEnrichment/csvEnrichment.api";

import { CustomEvent } from "../schema/customEvent";
import { LinkedinParams } from "../../LiPeople/components/LinkedinTagAutocomplete";
import { useAssignCsvToCustomEvent } from "./useAssignCsvToCustomEvent";

export interface AssignFilesModalProps {
	customEvent: CustomEvent | null;
	handleClose: () => void;
	/** Called once the files really are assigned — never on a refusal (PREC-3974). */
	handleAssigned: () => void;
}

/**
 * Attaching files that already exist to a source ("Select file" in the admin table).
 *
 * Extracted from `CustomEventTable` with PREC-3974: the save handler lived inside a 450-line render
 * tree no test could reach, so its `.unwrap()` sat there with nothing to catch a rejection and the
 * dialog closed before knowing whether the assignment had landed. It now closes only on success,
 * and a refusal says why.
 */
export const AssignFilesModal: FunctionComponent<AssignFilesModalProps> = ({
	customEvent,
	handleClose,
	handleAssigned,
}) => {
	const { t } = useTranslation();

	const [selected, setSelected] = useState<LinkedinParams[]>([]);

	const { setSearchValue: setCsvSearchValue, query: csvQuery } = useSearch();

	const { data: csvData } = useGetCsvEnrichmentsQuery({
		page: 1,
		limit: 10,
		searchValue: csvQuery,
	});

	const { assignCsvEnrichments, isLoading, renderMessageSnackbar } = useAssignCsvToCustomEvent();

	const handleSave = useCallback(async () => {
		if (!selected.length) {
			return;
		}

		const assigned = await assignCsvEnrichments(
			customEvent?.id ?? 0,
			selected.map((option) => parseInt(option.id))
		);

		if (!assigned) {
			return;
		}

		setSelected([]);
		handleAssigned();
	}, [selected, customEvent?.id, assignCsvEnrichments, handleAssigned]);

	const options: LinkedinParams[] = (csvData?.data || [])
		.map(
			(csv) =>
				({
					id: csv.id.toString(),
					name: `${csv.lists?.[0]?.name}: ${csv.name} (${csv.amountInCsv})`,
				}) as LinkedinParams
		)
		.filter((csv) => !selected.some((option) => option.id === csv.id));

	return (
		<Modal
			disableSave={selected.length === 0}
			handleClose={handleClose}
			handleSave={handleSave}
			isLoading={isLoading}
			isOpened
			size="md"
			submitButtonText={t("basics.confirm")}
			title={t("customEvent.reassign")}
		>
			<div className="mt-4 flex flex-col">
				<div className="text-sm mb-4">{t("customEvent.reassignDescription")}</div>
				{csvData ? (
					<Autocomplete
						multiple
						defaultValue={[]}
						getOptionLabel={(option) => option.name}
						id={"csv_selection"}
						options={options}
						popupIcon={<Icon className="text-gray-500 h-[24px] w-[15px]" icon={faChevronDown} />}
						renderInput={(params) => (
							<TextField
								sx={{
									width: "100%",
									minHeight: 48,
									marginBottom: 1,
									"& .MuiFilledInput-root": {
										minHeight: 48,
										marginBottom: 0,
										borderRadius: 1.5,
										paddingTop: "10px",
										paddingBottom: "10px !important",
										paddingLeft: 1.5,
									},
								}}
								{...params}
							/>
						)}
						size="medium"
						sx={{ width: "100%" }}
						onChange={(event, value) => setSelected(value)}
						onInputChange={(event, newInputValue) => setCsvSearchValue(newInputValue)}
					/>
				) : (
					""
				)}
			</div>

			{renderMessageSnackbar()}
		</Modal>
	);
};
